import classes from "./ProjectCarousel.module.css";
import { useState, useEffect, useCallback, useRef } from "react";
import { getCloudFrontImageUrl } from "../../util/cloudFrontUtils";
import { getUrl } from 'aws-amplify/storage';
import awsConfig from "../../config/aws-config";

// Helper function to extract YouTube/Vimeo video ID and convert to embed URL
const getEmbedUrl = (url) => {
  if (!url || typeof url !== 'string') return null;
  
  try {
    const urlObj = new URL(url);
    const hostname = urlObj.hostname.toLowerCase();
    
    // YouTube
    if (hostname.includes('youtube.com') || hostname.includes('youtu.be')) {
      let videoId = '';
      if (hostname.includes('youtu.be')) {
        videoId = urlObj.pathname.slice(1);
      } else {
        videoId = urlObj.searchParams.get('v');
      }
      if (videoId) {
        return `https://www.youtube.com/embed/${videoId}`;
      }
    }
    
    // Vimeo
    if (hostname.includes('vimeo.com')) {
      const videoId = urlObj.pathname.split('/').filter(p => p).pop();
      if (videoId) {
        return `https://player.vimeo.com/video/${videoId}`;
      }
    }
  } catch (error) {
    console.error('Error parsing video URL:', error);
  }
  
  return null;
};

// Embedded Video Player Component (YouTube/Vimeo)
const EmbeddedVideoPlayer = ({ item, index, isActive }) => {
  const iframeRef = useRef(null);
  const embedUrl = getEmbedUrl(item.url);
  
  if (!embedUrl) {
    return (
      <div className={classes.linkContainer}>
        <div className={classes.linkPlaceholder}>
          <span>🔗</span>
          <p>Video-Link nicht verfügbar</p>
        </div>
      </div>
    );
  }
  
  return (
    <div className={classes.embeddedVideoContainer}>
      <iframe
        ref={iframeRef}
        src={embedUrl}
        className={classes.embeddedVideo}
        frameBorder="0"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        allowFullScreen
        title={`Video ${index + 1}`}
      />
    </div>
  );
};

// Video Player Component
const VideoPlayer = ({ item, index, isActive }) => {
  const videoRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [showControls, setShowControls] = useState(true);
  const [thumbnailUrl, setThumbnailUrl] = useState(null);
  const [videoUrl, setVideoUrl] = useState(null);
  const [isLoadingVideo, setIsLoadingVideo] = useState(false);

  // Resolve video URL using AWS Amplify getUrl (same as MusicProjectModal)
  useEffect(() => {
    const resolveVideoUrl = async () => {
      // If already a full URL (not S3 direct), use it directly
      if (item.videoUrl && item.videoUrl.startsWith('http') && !item.videoUrl.includes('.s3.')) {
        setVideoUrl(item.videoUrl);
        return;
      }
      
      if (item.url && item.url.startsWith('http') && !item.url.includes('.s3.')) {
        setVideoUrl(item.url);
        return;
      }

      // Get video key from item (should be S3 key like "ed7a2b7d-c889-4927-a3f5-c133aa57e110.mp4")
      const videoKey = item.videoUrl || item.url;
      if (!videoKey) {
        setVideoUrl(null);
        return;
      }

      // Skip if it's already a full URL (including S3 URLs - we'll use getUrl instead)
      if (videoKey.startsWith('http')) {
        // Even if it's an S3 URL, try to extract the key and use getUrl
        try {
          const urlObj = new URL(videoKey);
          const pathParts = urlObj.pathname.split('/').filter(p => p);
          if (pathParts.length > 0) {
            const extractedKey = pathParts.slice(1).join('/'); // Remove 'public' prefix if present
            let keyForGetUrl = extractedKey.startsWith('public/') 
              ? extractedKey.replace('public/', '') 
              : extractedKey;
            
            const result = await getUrl({
              key: keyForGetUrl,
              options: {
                accessLevel: 'public',
                validateObjectExistence: false
              }
            });
            setVideoUrl(result.url.toString());
            return;
          }
        } catch (e) {
          // Fall through to normal processing
        }
      }

      setIsLoadingVideo(true);
      try {
        // Normalize the key - remove any duplicate 'public/' prefixes
        let cleanKey = videoKey;
        while (cleanKey.startsWith('public/public/')) {
          cleanKey = cleanKey.replace('public/public/', 'public/');
        }
        
        // IMPORTANT: getUrl with accessLevel: 'public' automatically adds 'public/' prefix
        // So we need to REMOVE the prefix if it exists before passing to getUrl
        let keyForGetUrl = cleanKey;
        if (keyForGetUrl.startsWith('public/')) {
          keyForGetUrl = keyForGetUrl.replace('public/', '');
        }
        
        // Use getUrl from AWS Amplify to get the proper URL
        const result = await getUrl({
          key: keyForGetUrl,
          options: {
            accessLevel: 'public',
            validateObjectExistence: false
          }
        });
        
        setVideoUrl(result.url.toString());
      } catch (error) {
        console.error('Error resolving video URL:', error);
        setVideoUrl(null);
      } finally {
        setIsLoadingVideo(false);
      }
    };

    resolveVideoUrl();
  }, [item.videoUrl, item.url]);

  // Get thumbnail URL - prioritize processedThumbnailUrl for videos
  useEffect(() => {
    // Check for processedThumbnailUrl first (from processedImages)
    const thumbnailSource = item.processedThumbnailUrl || item.thumbnailUrl || item.displayUrl;
    
    if (thumbnailSource) {
      // If it's a blob URL or full URL, use it directly
      if (thumbnailSource.startsWith('blob:') || thumbnailSource.startsWith('http')) {
        setThumbnailUrl(thumbnailSource);
      } else {
        // Otherwise, try to construct CloudFront URL
        try {
          const thumbUrl = getCloudFrontImageUrl(thumbnailSource, {
            width: 1200,
            height: 800,
            fit: 'cover'
          });
          setThumbnailUrl(thumbUrl || thumbnailSource);
        } catch {
          setThumbnailUrl(thumbnailSource);
        }
      }
    } else {
      setThumbnailUrl(null);
    }
  }, [item]);

  // Pause video when not active
  useEffect(() => {
    if (videoRef.current) {
      if (!isActive) {
        videoRef.current.pause();
        setIsPlaying(false);
      }
    }
  }, [isActive]);

  const handlePlayPause = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const handleVideoClick = () => {
    handlePlayPause();
  };

  const handleVideoPlay = () => {
    setIsPlaying(true);
    setShowControls(true); // Keep controls visible
  };

  const handleVideoPause = () => {
    setIsPlaying(false);
    setShowControls(true);
  };

  return (
    <div className={classes.videoContainer}>
      {isLoadingVideo && (
        <div className={classes.videoPlaceholder}>
          <span>🎥</span>
          <p>Video wird geladen...</p>
        </div>
      )}
      {!isLoadingVideo && !isPlaying && thumbnailUrl && (
        <div className={classes.videoThumbnail}>
          <img
            src={thumbnailUrl}
            alt={`Video-Vorschaubild ${index + 1}`}
            className={classes.slideImage}
          />
          <button 
            className={classes.playButton}
            onClick={handlePlayPause}
            aria-label="Play video"
          >
            <svg width="80" height="80" viewBox="0 0 80 80" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="40" cy="40" r="40" fill="rgba(0, 0, 0, 0.6)"/>
              <path d="M32 24L56 40L32 56V24Z" fill="white"/>
            </svg>
          </button>
        </div>
      )}
      {!isLoadingVideo && !isPlaying && !thumbnailUrl && videoUrl && (
        <div className={classes.videoPlaceholder}>
          <button 
            className={classes.playButton}
            onClick={handlePlayPause}
            aria-label="Play video"
          >
            <svg width="80" height="80" viewBox="0 0 80 80" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="40" cy="40" r="40" fill="rgba(0, 0, 0, 0.6)"/>
              <path d="M32 24L56 40L32 56V24Z" fill="white"/>
            </svg>
          </button>
          <span>🎥</span>
          <p>Video</p>
        </div>
      )}
      {videoUrl && (
        <video
          ref={videoRef}
          src={videoUrl}
          className={classes.videoElement}
          controls={showControls}
          onPlay={handleVideoPlay}
          onPause={handleVideoPause}
          onClick={handleVideoClick}
          playsInline
          preload="metadata"
          style={{ display: isPlaying ? 'block' : 'none' }}
        >
          Dein Browser unterstützt dieses Video nicht.
        </video>
      )}
      {!isLoadingVideo && !videoUrl && (
        <div className={classes.videoPlaceholder}>
          <span>🎥</span>
          <p>Video nicht verfügbar</p>
        </div>
      )}
    </div>
  );
};

export const ProjectCarousel = ({ mediaItems, startIndex, onClose }) => {
  const [currentIndex, setCurrentIndex] = useState(startIndex);
  const [isFadingOut, setIsFadingOut] = useState(false);
  
  // Enhanced drag/swipe state
  const [isDragging, setIsDragging] = useState(false);
  const [dragStartX, setDragStartX] = useState(0);
  const [dragOffset, setDragOffset] = useState(0);

  const containerRef = useRef(null);

  const nextImage = useCallback(() => {
    setCurrentIndex((prevIndex) => (prevIndex + 1) % mediaItems.length);
  }, [mediaItems.length]);

  const prevImage = useCallback(() => {
    setCurrentIndex((prevIndex) => (prevIndex - 1 + mediaItems.length) % mediaItems.length);
  }, [mediaItems.length]);

  const handleKeyDown = useCallback((event) => {
    if (event.key === 'ArrowLeft') {
      prevImage();
    } else if (event.key === 'ArrowRight') {
      nextImage();
    } else if (event.key === 'Escape') {
      handleClose();
    }
  }, [nextImage, prevImage]);

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleKeyDown]);

  // Enhanced mouse drag handlers
  const handleMouseDown = (e) => {
    if (!mediaItems?.length) return;
    setIsDragging(true);
    setDragStartX(e.clientX);
    setDragOffset(0);
    e.preventDefault();
  };

  const handleMouseMove = (e) => {
    if (!isDragging || !mediaItems?.length) return;
    const currentX = e.clientX;
    const diff = currentX - dragStartX;
    const container = containerRef.current;
    const containerWidth = container ? container.offsetWidth : 1;
    const offsetPercent = (diff / containerWidth) * 100;
    
    // Hard block edge drag beyond bounds
    let constrained = offsetPercent;
    if (currentIndex === 0 && offsetPercent > 0) constrained = 0;
    if (currentIndex === mediaItems.length - 1 && offsetPercent < 0) constrained = 0;
    
    setDragOffset(constrained);
  };

  const commitDrag = () => {
    if (!isDragging || !mediaItems?.length) return;
    const threshold = 5; // percent
    const dragDistance = Math.abs(dragOffset);
    if (dragDistance > threshold) {
      if (dragOffset > 0 && currentIndex > 0) {
        setCurrentIndex(currentIndex - 1);
      } else if (dragOffset < 0 && currentIndex < mediaItems.length - 1) {
        setCurrentIndex(currentIndex + 1);
      }
    }
    setIsDragging(false);
    setDragOffset(0);
  };

  const handleMouseUp = () => commitDrag();
  const handleMouseLeave = () => { if (isDragging) commitDrag(); };

  // Enhanced touch handlers
  const handleTouchStart = (e) => {
    if (!mediaItems?.length) return;
    const touch = e.touches[0];
    setIsDragging(true);
    setDragStartX(touch.clientX);
    setDragOffset(0);
  };

  const handleTouchMove = (e) => {
    if (!isDragging || !mediaItems?.length) return;
    // Prevent vertical page scroll while swiping carousel
    if (e.cancelable) e.preventDefault();
    const touch = e.touches[0];
    const currentX = touch.clientX;
    const diff = currentX - dragStartX;
    const container = containerRef.current;
    const containerWidth = container ? container.offsetWidth : 1;
    const offsetPercent = (diff / containerWidth) * 100;
    
    // Hard block edge drag beyond bounds
    let constrained = offsetPercent;
    if (currentIndex === 0 && offsetPercent > 0) constrained = 0;
    if (currentIndex === mediaItems.length - 1 && offsetPercent < 0) constrained = 0;
    
    setDragOffset(constrained);
  };

  const handleTouchEnd = () => commitDrag();

  const handleClose = useCallback(() => {
    setIsFadingOut(true);
  }, []);

  useEffect(() => {
    if (isFadingOut) {
      const timer = setTimeout(() => {
        onClose();
      }, 400);

      return () => clearTimeout(timer);
    }
  }, [isFadingOut, onClose]);

  const currentItem = mediaItems[currentIndex];

  return (
    <div className={`${classes.carouselContainer} ${isFadingOut ? classes.fadeOut : ''}`}>
      <header>
        <div onClick={handleClose} className={classes.closeButton}>
          <svg xmlns="http://www.w3.org/2000/svg" width="19" height="20" viewBox="0 0 19 20" fill="none">
            <path d="M1 1L18 18.5" stroke="white" strokeWidth="1.70431" strokeMiterlimit="10"/>
            <path d="M18 1L1 18.5" stroke="white" strokeWidth="1.70431" strokeMiterlimit="10"/>
          </svg>
          <span className="mediumLarge">Schließen</span>
        </div>
        <span className={classes.tabletMode}>{currentIndex + 1}/{mediaItems.length}</span>
      </header>
      <div 
        ref={containerRef} 
        className={`${classes.carouselContent} ${isDragging ? classes.dragging : ''}`}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseLeave}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        style={{ 
          touchAction: 'pan-x', 
          overflow: 'hidden'
        }}
      >
        <button onClick={prevImage} className={classes.navigationButton}>
          <svg xmlns="http://www.w3.org/2000/svg" width="46" height="46" viewBox="0 0 46 46" fill="none">
            <circle cx="23" cy="23" r="22" strokeWidth="2"/>
            <line x1="26.5996" y1="14.601" x2="17.601" y2="23.5996" stroke="white" strokeWidth="1.7"/>
            <line x1="17.6256" y1="22.4215" x2="26.6241" y2="32.1699" stroke="white" strokeWidth="1.7"/>
          </svg>
        </button>
        <div className={classes.mainContainer}>
          <div className={classes.imageContainer}>
            <div 
              className={`${classes.imageSlider} ${isDragging ? classes.dragging : ''}`}
              style={{
                transform: `translateX(${-100 * currentIndex + dragOffset}%)`,
                transition: isDragging ? 'none' : 'transform 0.2s ease-out'
              }}
            >
              {mediaItems.map((item, index) => (
                <div key={index} className={classes.imageSlide}>
                  {item.isLink ? (
                    <EmbeddedVideoPlayer 
                      item={item} 
                      index={index}
                      isActive={index === currentIndex}
                    />
                  ) : item.isVideo ? (
                    <VideoPlayer 
                      item={item} 
                      index={index}
                      isActive={index === currentIndex}
                    />
                  ) : item.displayUrl ? (
                    <img
                      src={item.displayUrl}
                      alt={`Image ${index + 1}`}
                      className={classes.slideImage}
                    />
                  ) : null}
                </div>
              ))}
            </div>
          </div>
        </div>
        <button onClick={nextImage} className={classes.navigationButton}>
          <svg xmlns="http://www.w3.org/2000/svg" width="46" height="46" viewBox="0 0 46 46" fill="none">
            <circle cx="23" cy="23" r="22" transform="rotate(180 23 23)" strokeWidth="2"/>
            <line x1="19.4004" y1="31.399" x2="28.399" y2="22.4004" stroke="white" strokeWidth="1.7"/>
            <line x1="28.3744" y1="23.5785" x2="19.3759" y2="13.8301" stroke="white" strokeWidth="1.7"/>
          </svg>
        </button>
      </div>
      <footer className={classes.footer}>
        <span className={classes.laptopMode}>{currentIndex + 1}/{mediaItems.length}</span>
      </footer>
    </div>
  );
};

export default ProjectCarousel;
